import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Trophy, UserPlus, Users, X } from 'lucide-react';
import { useScoring } from '../../../hooks/useScoring';
import { getVerdict, Verdict } from '../../../utils/scoring';
import BuzzerStage from '../Buzzer/BuzzerStage';
import ScoringPanel from '../../Elements/Scoring/ScoringPanel';
import VerdictButtons from '../../Elements/Scoring/VerdictButtons';

const SCORING_STORAGE_KEY = '3sual.scoring.state';
const ROSTER_STORAGE_KEY = '3sual.scoring.roster';

const nameKey = (name: string) => name.trim().toLowerCase();

const readRoster = (): string[] => {
  try {
    const saved = JSON.parse(localStorage.getItem(ROSTER_STORAGE_KEY) ?? '[]');
    return Array.isArray(saved) ? saved.filter((n): n is string => typeof n === 'string') : [];
  } catch {
    return [];
  }
};

const panelStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '1rem',
};

const ghostBtnStyle: React.CSSProperties = {
  borderRadius: '0.75rem',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.10)',
};

/**
 * Standalone point counter — the same scoring engine the buzzer host uses, but
 * the moderator keeps the roster by hand and judges whoever answered. Lives
 * entirely in this browser (survives reloads), no game room needed.
 */
const Scoring: React.FC = () => {
  const scoring = useScoring(SCORING_STORAGE_KEY);
  const [roster, setRoster] = useState<string[]>(readRoster);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem(ROSTER_STORAGE_KEY, JSON.stringify(roster));
    } catch {
      // storage unavailable (private mode) — keep the in-memory roster
    }
  }, [roster]);

  // Roster order first, then anyone who still has points but is off the roster
  // (e.g. brought back by undo after being removed).
  const players = useMemo(() => {
    const keys = new Set(roster.map(nameKey));
    const extras = Object.values(scoring.state.players)
      .map((p) => p.name)
      .filter((name) => !keys.has(nameKey(name)));
    return [...roster, ...extras];
  }, [roster, scoring.state.players]);

  const trimmed = newName.trim();
  const duplicate = players.some((p) => nameKey(p) === nameKey(trimmed));

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!trimmed || duplicate) return;
    setRoster((prev) => [...prev, trimmed]);
    setNewName('');
  };

  const handleRemove = (name: string) => {
    setRoster((prev) => prev.filter((p) => nameKey(p) !== nameKey(name)));
    scoring.removePlayer(name);
  };

  const handleVerdict = (name: string, verdict: Verdict) => {
    if (getVerdict(scoring.state, name)) return;
    scoring.judge(name, verdict);
  };

  return (
    <BuzzerStage>
      <Helmet>
        <title>Xal sayğacı | 3sual.az</title>
      </Helmet>

      <header className="relative z-10 flex items-center justify-between gap-3 px-4 pb-3 pt-[calc(1.25rem+env(safe-area-inset-top))] sm:px-10 sm:pt-[calc(1.75rem+env(safe-area-inset-top))]">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center sm:h-10 sm:w-10"
            style={{
              borderRadius: '0.75rem',
              background: 'rgba(251,191,36,0.10)',
              border: '1px solid rgba(251,191,36,0.25)',
            }}
          >
            <Trophy className="h-4 w-4 text-amber-300 sm:h-5 sm:w-5" />
          </div>
          <h1 className="truncate text-xl font-black text-white sm:text-2xl">Xal sayğacı</h1>
        </div>
        <Link
          to="/"
          title="Bağla"
          aria-label="Bağla"
          style={ghostBtnStyle}
          className="flex h-9 w-9 shrink-0 items-center justify-center gap-1.5 text-sm font-semibold text-white/60 transition hover:text-white sm:h-10 sm:w-auto sm:px-3"
        >
          <X className="h-4 w-4" />
          <span className="hidden sm:inline">Bağla</span>
        </Link>
      </header>

      <main className="relative z-10 mx-auto flex w-full max-w-6xl flex-col gap-5 px-4 pb-[calc(2rem+env(safe-area-inset-bottom))] sm:px-10">
        {/* Players + verdicts */}
        <div style={panelStyle} className="overflow-hidden">
          <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3.5 sm:px-5">
            <Users className="h-5 w-5 text-sky-300" />
            <h2 className="font-bold text-white">Oyunçular</h2>
            <span className="text-sm text-white/40">({players.length})</span>
          </div>

          <form onSubmit={handleAdd} className="flex gap-2 border-b border-white/5 p-4">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Oyunçu və ya komanda adı"
              aria-label="Oyunçu adı"
              maxLength={40}
              className="min-w-0 flex-1 rounded-[0.625rem] border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-white/30 focus:border-sky-400/60 focus:outline-none"
            />
            <button
              type="submit"
              disabled={!trimmed || duplicate}
              title={duplicate ? 'Bu ad artıq var' : 'Əlavə et'}
              style={{ borderRadius: '0.625rem' }}
              className="inline-flex shrink-0 items-center gap-1.5 bg-sky-500 px-3 text-sm font-bold text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <UserPlus className="h-4 w-4" />
              <span className="hidden sm:inline">Əlavə et</span>
            </button>
          </form>

          <div className="flex flex-col gap-2 p-4">
            {players.length === 0 ? (
              <p className="py-6 text-center text-sm text-white/30">Başlamaq üçün oyunçu əlavə edin.</p>
            ) : (
              players.map((name) => (
                <div
                  key={nameKey(name)}
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5"
                >
                  <span className="min-w-0 flex-1 truncate text-lg font-bold">{name}</span>
                  <VerdictButtons
                    points={scoring.value}
                    verdict={getVerdict(scoring.state, name)}
                    onJudge={(verdict) => handleVerdict(name, verdict)}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemove(name)}
                    title="Oyunçunu sil («Geri al» xalı qaytarır)"
                    aria-label={`${name}: sil`}
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[0.625rem] text-white/30 transition hover:bg-white/10 hover:text-rose-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <ScoringPanel scoring={scoring} roster={players} />
      </main>
    </BuzzerStage>
  );
};

export default Scoring;
